import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardActions,
  Typography,
  Chip,
  Stack,
  TextField,
  Button,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  Checkbox,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Badge,
  TableContainer,
  TableHead,
  Paper,
  Table,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import EmailIcon from "@mui/icons-material/Email";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import MessageIcon from "@mui/icons-material/Message";
import DownloadIcon from "@mui/icons-material/Download";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import ClearIcon from "@mui/icons-material/Clear";
import CloseIcon from "@mui/icons-material/Close";
import NotesIcon from "@mui/icons-material/Notes";
import AutoSizer from "react-virtualized-auto-sizer";
import { FixedSizeGrid as VirtualGrid } from "react-window";

// ---------- Utils ----------
const toDate = (d) => (d ? new Date(d) : null);
const fmt = (d) => (d ? new Date(d).toISOString().slice(0, 10) : "—");
const nightsBetween = (start, end) => {
  const s = toDate(start);
  const e = toDate(end);
  if (!s || !e) return 0;
  const ms = e.setHours(0, 0, 0, 0) - s.setHours(0, 0, 0, 0);
  return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
};
const money = (n) => (typeof n === "number" ? n.toFixed(0) : String(n || 0));

function exportCSV(rows) {
  const headers = [
    "reservationId",
    "Nombre",
    "Email",
    "Telefono",
    "Origen",
    "Estado",
    "EstadoInterno",
    "Ingreso",
    "Salida",
    "Noches",
    "Importe",
    "Seña",
    "Saldo",
  ];
  const lines = rows.map((r) =>
    [
      r.reservationId,
      r.fullName,
      r.email,
      r.numberphone,
      r.origin,
      r.estado,
      r.estadoIn,
      fmt(r.startDate),
      fmt(r.endDate),
      r.nights,
      r.amount,
      r.seña,
      r.balance,
    ]
      .map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`)
      .join(",")
  );
  const csv = [headers.join(","), ...lines].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `crm_ubika_${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function useNotes() {
  const KEY = "ubika_crm_notes_v1";
  const [notes, setNotes] = useState({});
  useEffect(() => {
    try {
      const raw = localStorage.getItem(KEY);
      if (raw) setNotes(JSON.parse(raw));
    } catch {}
  }, []);
  const save = (id, text) => {
    const next = { ...notes, [id]: text };
    setNotes(next);
    try {
      localStorage.setItem(KEY, JSON.stringify(next));
    } catch {}
  };
  const get = (id) => notes[id] || "";
  return { get, save };
}

// ---------- Componente ----------
export default function ClientesDashboard({ reservations = [] }) {
  // Filtros / estado UI
  const [q, setQ] = useState("");
  const [origin, setOrigin] = useState("Todos");
  const [estado, setEstado] = useState("Todos");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [drawer, setDrawer] = useState({ open: false, item: null });

  const notes = useNotes();

  // Normalización por reserva (ID único reservationId)
  const { rows, kpis, origins, estados } = useMemo(() => {
    const byId = new Map(); // <reservationId, item>
    const originsSet = new Set(["Todos"]);
    const estadosSet = new Set(["Todos"]);

    (reservations || []).forEach((r) => {
      const id = r?.reservationId || r?._id || "";
      if (!id) return;

      const item = {
        reservationId: id,
        fullName: r?.fullName || "Sin nombre",
        email: r?.email || "",
        numberphone: r?.numberphone || "",
        origin: r?.origin || "Desconocido",
        startDate: r?.startDate || null,
        endDate: r?.endDate || null,
        createdAt: r?.createdAt || null,
        amount: Number(r?.amount || 0),
        seña: Number(r?.seña || 0),
        estado: r?.estado || "pendiente",
        estadoIn: r?.estadoIn || "pendiente",
        paymentMethod: r?.paymentMethod || "",
      };

      // Filtros por fecha (a nivel reserva)
      if (
        from &&
        toDate(item.startDate) &&
        toDate(item.startDate) < toDate(from)
      )
        return;
      if (to && toDate(item.endDate) && toDate(item.endDate) > toDate(to))
        return;

      item.nights = nightsBetween(item.startDate, item.endDate);
      item.balance = Math.max(0, item.amount - item.seña);

      // Deduplicación: quedarse con la versión más nueva por createdAt (o endDate)
      const existing = byId.get(id);
      if (!existing) {
        byId.set(id, item);
      } else {
        const exTime =
          toDate(existing.createdAt) || toDate(existing.endDate) || new Date(0);
        const itTime =
          toDate(item.createdAt) || toDate(item.endDate) || new Date(0);
        if (itTime >= exTime) byId.set(id, item);
      }
    });

    // Construir arrays y KPIs a partir de la lista única
    const list = Array.from(byId.values());

    let totReservas = 0;
    let totNoches = 0;
    let totIngresos = 0;

    list.forEach((i) => {
      originsSet.add(i.origin);
      estadosSet.add(i.estado);
      totReservas += 1;
      totNoches += i.nights || 0;
      totIngresos += i.amount || 0;
    });

    return {
      rows: list,
      origins: Array.from(originsSet),
      estados: Array.from(estadosSet),
      kpis: {
        reservas: totReservas,
        noches: totNoches,
        ingresos: totIngresos,
        clientesAContactar: list.filter((i) => !i.email && !i.numberphone)
          .length,
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reservations, from, to]);

  // Búsqueda + filtros de canal/estado + texto
  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (origin !== "Todos" && r.origin !== origin) return false;
      if (estado !== "Todos" && r.estado !== estado) return false;
      if (ql) {
        const hay =
          r.fullName?.toLowerCase().includes(ql) ||
          r.email?.toLowerCase().includes(ql) ||
          r.numberphone?.toLowerCase().includes(ql) ||
          r.origin?.toLowerCase().includes(ql) ||
          String(r.reservationId).toLowerCase().includes(ql);
        if (!hay) return false;
      }
      return true;
    });
  }, [rows, origin, estado, q]);

  // Selecciones
  const toggleOne = (id) => {
    const next = new Set(selectedIds);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelectedIds(next);
  };
  const selectAllFiltered = () => {
    setSelectedIds(new Set(filtered.map((r) => r.reservationId)));
  };
  const clearSelection = () => setSelectedIds(new Set());

  // Acciones
  const doBulkEmail = () => {
    const emails = filtered
      .filter((r) => selectedIds.has(r.reservationId))
      .map((r) => r.email)
      .filter(Boolean);
    if (!emails.length) return;
    window.location.href = `mailto:${emails.join(",")}`;
  };
  const doBulkCSV = () => {
    const rowsSel = filtered.filter((r) => selectedIds.has(r.reservationId));
    if (!rowsSel.length) return;
    exportCSV(rowsSel);
  };

  const clearFilters = () => {
    setQ("");
    setOrigin("Todos");
    setEstado("Todos");
    setFrom("");
    setTo("");
  };

  // KPIs (arriba)
  const Kpi = ({ title, value, subtitle }) => (
    <Card sx={{ borderRadius: "16px" }}>
      <CardContent>
        <Typography variant="overline">{title}</Typography>
        <Typography variant="h5" sx={{ fontWeight: 800 }}>
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* KPIs */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} md={3}>
          <Kpi title="Reservas" value={kpis.reservas} />
        </Grid>
        <Grid item xs={6} md={3}>
          <Kpi title="Noches" value={kpis.noches} />
        </Grid>

        <Grid item xs={6} md={3}>
          <Kpi
            title="Faltan datos"
            value={kpis.clientesAContactar}
            subtitle="sin email y teléfono"
          />
        </Grid>
      </Grid>

      {/* Filtros */}
      <Card sx={{ mb: 2, borderRadius: "16px" }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Buscar por nombre, email, teléfono, canal o ID"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1 }} /> }}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>Canal</InputLabel>
                <Select
                  label="Canal"
                  value={origin}
                  onChange={(e) => setOrigin(e.target.value)}
                >
                  {origins.map((o) => (
                    <MenuItem key={o} value={o}>
                      {o}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>Estado</InputLabel>
                <Select
                  label="Estado"
                  value={estado}
                  onChange={(e) => setEstado(e.target.value)}
                >
                  {estados.map((s) => (
                    <MenuItem key={s} value={s}>
                      {s}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                type="date"
                label="Desde"
                InputLabelProps={{ shrink: true }}
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                type="date"
                label="Hasta"
                InputLabelProps={{ shrink: true }}
                value={to}
                onChange={(e) => setTo(e.target.value)}
                fullWidth
              />
            </Grid>

            <Grid item xs={12}>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                flexWrap="wrap"
              >
                <Tooltip title="Seleccionar todo (filtrado)">
                  <Button size="small" onClick={selectAllFiltered}>
                    Seleccionar todo
                  </Button>
                </Tooltip>
                <Tooltip title="Limpiar selección">
                  <Button size="small" onClick={clearSelection}>
                    Limpiar selección
                  </Button>
                </Tooltip>
                <Divider flexItem orientation="vertical" sx={{ mx: 1 }} />
                <Tooltip title="Enviar email a seleccionados">
                  <span>
                    <Button
                      size="small"
                      startIcon={<EmailIcon />}
                      disabled={[...selectedIds].length === 0}
                      onClick={doBulkEmail}
                    >
                      Email masivo
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip title="Exportar CSV de seleccionados">
                  <span>
                    <Button
                      size="small"
                      startIcon={<DownloadIcon />}
                      disabled={[...selectedIds].length === 0}
                      onClick={doBulkCSV}
                    >
                      Exportar CSV
                    </Button>
                  </span>
                </Tooltip>
                <Divider flexItem orientation="vertical" sx={{ mx: 1 }} />
                <Tooltip title="Limpiar filtros">
                  <IconButton onClick={clearFilters}>
                    <ClearIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Grid de cards */}
      {/* === Grid de cards (virtualizado) === */}
      <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox"></TableCell>
              <TableCell>Nombre</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Teléfono</TableCell>
              <TableCell>Notas</TableCell>
              <TableCell align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map((item) => {
              const selected = selectedIds.has(item.reservationId);
              const saldo = item.balance;

              return (
                <TableRow key={item.reservationId} hover selected={selected}>
                  {/* Selección */}
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selected}
                      onChange={() => toggleOne(item.reservationId)}
                    />
                  </TableCell>

                  {/* Datos principales */}
                  <TableCell sx={{ fontWeight: 600 }}>
                    {item.fullName}
                  </TableCell>
                  <TableCell>{item.email || "Sin email"}</TableCell>
                  <TableCell>{item.numberphone || "Sin teléfono"}</TableCell>
                  {/* Notas */}
                  <TableCell sx={{ minWidth: 200 }}>
                    <TextField
                      fullWidth
                      placeholder="Agregar nota…"
                      multiline
                      minRows={1}
                      maxRows={3}
                      value={notes.get(item.reservationId)}
                      onChange={(e) =>
                        notes.save(item.reservationId, e.target.value)
                      }
                    />
                  </TableCell>

                  {/* Acciones */}
                  <TableCell align="center">
                    <Tooltip title="Enviar email">
                      <span>
                        <IconButton
                          size="small"
                          disabled={!item.email}
                          onClick={() =>
                            (window.location.href = `mailto:${item.email}`)
                          }
                        >
                          <EmailIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Enviar WhatsApp">
                      <span>
                        <IconButton
                          size="small"
                          disabled={!item.numberphone}
                          onClick={() =>
                            window.open(
                              `https://wa.me/${String(item.numberphone).replace(
                                /\D/g,
                                ""
                              )}`,
                              "_blank"
                            )
                          }
                        >
                          <WhatsAppIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Abrir mensajes">
                      <IconButton
                        size="small"
                        onClick={() => setDrawer({ open: true, item })}
                      >
                        <MessageIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Drawer Mensajes / Detalle */}
      <Drawer
        anchor="right"
        open={drawer.open}
        onClose={() => setDrawer({ open: false, item: null })}
      >
        <Box sx={{ width: { xs: 360, md: 520 }, p: 2 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="h6">Mensajes del cliente</Typography>
            <IconButton onClick={() => setDrawer({ open: false, item: null })}>
              <CloseIcon />
            </IconButton>
          </Stack>
          <Divider sx={{ my: 1.5 }} />
          {drawer.item ? (
            <>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                {drawer.item.fullName}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {drawer.item.email || "Sin email"} ·{" "}
                {drawer.item.numberphone || "Sin teléfono"}
              </Typography>
              <Stack
                direction="row"
                spacing={1}
                sx={{ mb: 2, flexWrap: "wrap" }}
              >
                <Chip size="small" label={drawer.item.origin} />
                <Chip size="small" label={drawer.item.estado} />
              </Stack>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Historial (placeholder)
              </Typography>
              <Card
                variant="outlined"
                sx={{ borderRadius: "12px", maxHeight: 260, overflow: "auto" }}
              >
                <List dense>
                  <ListItem>
                    <ListItemText
                      primary="Aquí verás mensajes integrados (Booking/Airbnb/Hostelworld/Web/WhatsApp)"
                      secondary="Conexión pendiente de la sección Mensajes."
                    />
                  </ListItem>
                </List>
              </Card>
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<MessageIcon />}
                >
                  Abrir Mensajes
                </Button>
                <Button size="small" variant="text">
                  Conectar canales
                </Button>
              </Stack>
            </>
          ) : null}
        </Box>
      </Drawer>
    </Box>
  );
}
